import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react'
import { T, glass } from '../pages/shared'

type ToastType = 'success' | 'error' | 'warning' | 'info'
interface Toast { id: number; message: string; type: ToastType }
interface ToastCtx { show: (msg: string, type?: ToastType) => void }

const Ctx = createContext<ToastCtx>({ show: () => {} })

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 size={16} strokeWidth={2.25} aria-hidden />,
  error:   <XCircle size={16} strokeWidth={2.25} aria-hidden />,
  warning: <AlertTriangle size={16} strokeWidth={2.25} aria-hidden />,
  info:    <Info size={16} strokeWidth={2.25} aria-hidden />,
}
const COLORS: Record<ToastType, [string, string, string]> = {
  success: [T.olivine,    'rgba(122,140,46,0.18)',  'rgba(122,140,46,0.35)'],
  error:   [T.canyon,     T.canyonDim,              'rgba(212,98,26,0.35)'],
  warning: [T.amber,      T.amberDim,               'rgba(201,162,39,0.35)'],
  info:    [T.sand,       'rgba(184,170,130,0.12)', 'rgba(184,170,130,0.3)'],
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const [announce, setAnnounce] = useState('')
  const nextIdRef = useRef(0)

  const show = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++nextIdRef.current
    setAnnounce(message)
    setToasts(ts => [...ts, { id, message, type }])
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 4000)
  }, [])

  return (
    <Ctx.Provider value={{ show }}>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announce}
      </div>
      {children}
      {/* Portal-style fixed container */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
        {toasts.map(t => {
          const [color, bg, border] = COLORS[t.type]
          return (
            <div key={t.id} className="fade-up" style={{
              ...glass,
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px', borderRadius: T.rSm,
              border: `1px solid ${border}`,
              boxShadow: `var(--glass-card-outer), var(--glass-card-inset), 0 12px 40px rgba(0,0,0,0.25)`,
              minWidth: 280, maxWidth: 400,
              pointerEvents: 'all',
            }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
                {ICONS[t.type]}
              </div>
              <span style={{ fontSize: 13, color: T.textPrimary, fontFamily: T.fontBody, lineHeight: 1.4, flex: 1 }}>{t.message}</span>
              <div style={{ width: 3, height: 36, borderRadius: 2, background: color, flexShrink: 0, opacity: 0.7 }} />
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}

export const useToast = () => useContext(Ctx)
